import React, { useContext, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import MainServices from "../../../../services/MainServices";
import { AuthContext } from "../../../../context/AuthContext";
import { notifySuccess, notifyWarn } from "../../../../assets/elements/Toast";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import passwordEye from "../../../../assets/images/password.svg";

const ForgotPassword = () => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const { changePassword } = MainServices();

  // State for password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation schema using Yup with translation wrapper
  const passwordValidationSchema = Yup.object().shape({
    currentPassword: Yup.string()
      .required(t("Previous Password is required"))
      .min(8, t("Password must be at least 8 characters"))
      .max(64, t("Password must not exceed 64 characters")),

    newPassword: Yup.string()
      .required(t("New Password is required"))
      .min(8, t("New password must be at least 8 characters"))
      .max(64, t("Password must not exceed 64 characters"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\W_]{8,}$/,
        t(
          "Password must include uppercase, lowercase, number, and special character"
        )
      )
      .notOneOf(
        [Yup.ref("currentPassword")],
        t("New password must be different from current password")
      ),

    confirmPassword: Yup.string()
      .required(t("Confirm Password is required"))
      .oneOf([Yup.ref("newPassword")], t("Passwords must match")),
  });

  const handlePasswordChange = async (values, { setSubmitting, setStatus }) => {
    try {
      const response = await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmNewPassword: values.confirmPassword,
      });

      if (response.data) {
        notifySuccess(t("Password Changed Successfully"));
        setStatus({
          type: "success",
          message: t("Password successfully updated"),
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: t("An unexpected error occurred"),
      });
      notifyWarn(t("An unexpected error occurred"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="min-h-screen bg-[#F9FAFB] p-4 md:p-8 text-black">
          <div className="max-w-full mx-auto">
            <div className="border-[1px] border-[#EAECF0] rounded-md mb-4 p-4 md:p-6">
              <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">
                {t("Account Settings")}
              </h1>
              <p className="text-[#475467] text-sm md:text-base">
                {t(
                  "Keep track of all your Account Updates in one centralized place for better organization and decision-making."
                )}
              </p>
            </div>
            <div className="border rounded-md border-[#EAECF0] bg-[#F9FAFB] p-6 md:p-8 text-black">
              <div className="max-w-[500px] flex justify-start flex-col">
                <h1 className="text-2xl md:text-3xl font-bold mb-4">
                  {t("Change Password")}
                </h1>
                <p className="text-black mb-8">
                  {t("Here you can change your account security.")}
                </p>

                <Formik
                  initialValues={{
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  }}
                  validationSchema={passwordValidationSchema}
                  onSubmit={handlePasswordChange}
                >
                  {({ isSubmitting, status }) => (
                    <Form className="space-y-6">
                      {/* Current Password */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold">
                          {t("Current Password")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Field
                            type={showCurrentPassword ? "text" : "password"}
                            name="currentPassword"
                            placeholder={t("Enter Previous Password")}
                            className="w-full px-4 py-3 pr-12 rounded-lg bg-[#F9FAFB] border border-[#f2f4f7] focus:outline-none focus:ring-2 focus:ring-[#f2f4f7]"
                          />
                          <span
                            className={`password-icon ${
                              showCurrentPassword ? "" : "crossed-eye"
                            } absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer`}
                            onClick={() =>
                              setShowCurrentPassword(!showCurrentPassword)
                            }
                          >
                            <img
                              src={passwordEye}
                              alt="Toggle password visibility"
                              className="w-5 h-5"
                            />
                          </span>
                        </div>
                        <ErrorMessage
                          name="currentPassword"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* New Password */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold">
                          {t("New Password")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Field
                            type={showNewPassword ? "text" : "password"}
                            name="newPassword"
                            placeholder={t("Enter New Password")}
                            className="w-full px-4 py-3 pr-12 rounded-lg bg-[#F9FAFB] border border-[#f2f4f7] focus:outline-none focus:ring-2 focus:ring-[#f2f4f7]"
                          />
                          <span
                            className={`password-icon ${
                              showNewPassword ? "" : "crossed-eye"
                            } absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer`}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            <img
                              src={passwordEye}
                              alt="Toggle password visibility"
                              className="w-5 h-5"
                            />
                          </span>
                        </div>
                        <ErrorMessage
                          name="newPassword"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Confirm Password */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold">
                          {t("Confirm Password")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Field
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            placeholder={t("Enter Confirm Password")}
                            className="w-full px-4 py-3 pr-12 rounded-lg bg-[#F9FAFB] border border-[#f2f4f7] focus:outline-none focus:ring-2 focus:ring-[#f2f4f7]"
                          />
                          <span
                            className={`password-icon ${
                              showConfirmPassword ? "" : "crossed-eye"
                            } absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer`}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            <img
                              src={passwordEye}
                              alt="Toggle password visibility"
                              className="w-5 h-5"
                            />
                          </span>
                        </div>
                        <ErrorMessage
                          name="confirmPassword"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Status Message */}
                      {status && (
                        <div
                          className={`
                          text-sm p-2 rounded 
                          ${
                            status.type === "success"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }
                        `}
                        >
                          {status.message}
                        </div>
                      )}

                      {/* Submit Button */}
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="py-2 px-4 bg-[#B99A51] text-white font-semibold rounded-lg hover:bg-[#B99A52] transition-colors disabled:opacity-50"
                        >
                          {isSubmitting ? t("Updating...") : t("Save & Update")}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
